import { get, includes, merge, template as templ } from 'lodash';
import * as qs from 'qs';
import { RequestAdapters, RequestInputs } from '../RequestAdapter';
import { SIGNING_HOST_HEADER, SIGNING_PATH_HEADER } from '../SignedAxiosAdapter';
import { TokenTypes } from '../store';
import { CoreService } from './CoreService';

export enum RequestMethod {
  PUT = 'PUT',
  POST = 'POST',
  DELETE = 'DELETE',
  GET = 'GET',
}

export interface AuthRequestInput {
  readonly tokenType?: TokenRequirements;
  readonly headers?: any;
  readonly url?: string;
  readonly detail?: boolean;
}

export interface RequestOptions extends AuthRequestInput {
  readonly method?: RequestMethod;
  readonly params?: Record<string, unknown>;
  readonly query?: Record<string, unknown>;
  readonly data?: any;
  readonly queryOptions?: Record<string, unknown>; //
  readonly sign?: boolean;
  readonly useCachedConfig?: boolean;
  readonly role?: string;
}

export interface IUrlParams {
  url: string;
  headers: Headers;
  role?: string;
}

export enum HeaderKeys {
  ACCEPT = 'Accept',
  CONTENT_TYPE = 'Content-Type',
  X_AMZ_TARGET = 'X-Amz-Target',
  AUTHORIZATION = 'Authorization',
};

export type Headers = {
  [key: string]: string;
};

export type TokenRequirements = 'none' | TokenTypes;

export interface BaseServiceConstructor {
  readonly configPath?: string;
}

export interface IBaseRequestInput {
  headers?: Headers;
  url?: string;
  method?: string;
  data?: any;
  detail?: boolean;
  adapter?: RequestAdapters;
  role?: string;
}

export interface Header {
  readonly name: string;
  readonly value: string;
}

export interface RawRequestInput extends RequestInputs {
  readonly adapter?: RequestAdapters;
}

export interface IResolvedSigningOptions {
  headers: Record<string, any>;
  role?: string;
}

export class BaseService {
  private _configPath: string | undefined;
  private _coreInstance: CoreService | undefined;
  private _globalHeaders?: Header[];

  constructor({ configPath }: BaseServiceConstructor) {
    this._configPath = configPath;
  }

  globalHeaders(): Headers {
    const headers: Headers = {};

    if (!this._globalHeaders) {
      const envHeaders = process.env.SANS_SDK_GLOBAL_HEADERS;
      this._globalHeaders = envHeaders ? JSON.parse(envHeaders) : [];
    }

    for (const { name, value } of this._globalHeaders!) {
      headers[name] = value;
    }
    return headers;
  }

  async config() {
    if (this._configPath) {
      const config = await this.core?.api.config.find(this._configPath);
      return config;
    }
    return undefined;
  }

  async clearTokensAndLogout(): Promise<any> {
    const { api } = this.core;
    api.auth.authStore?.logout();
    await api.tokens.clear();
  }

  get core(): CoreService {
    if (!this._coreInstance) throw 'CoreService not defined'; // TODO fix exceptions
    return this._coreInstance;
  }

  set core(coreInstance: CoreService) {
    this._coreInstance = coreInstance;
  }

  get api() {
    return this.core.api;
  }

  // @ts-ignore
  async rawRequest({ adapter, ...options }: RawRequestInput): Promise<any> {
    const { url } = options as any;
    if (url) {
      const { hostname } = new URL(url as string);
      const globalHeaders = hostname.endsWith('.sans.org') ? { ...this.globalHeaders() } : {};
      options.headers = {
        ...globalHeaders,
        ...options.headers,
      };
    }

    // TODO: Consider moving this to somewhere else.
    const authErrors = ['invalid_token', 'invalid_grant', 'unauthorized_client', 'invalid_client'];
    const authErrorStatus = [400, 401];

    try {
      const rawResponse = await this.core.requestAdapter(adapter).request(options);

      return rawResponse;
    } catch (e) {
      // @ts-ignore TODO rewrite this logic
      if (e?.code === 'ECONNABORTED') {
        throw 'The service is temporily unavailable, please try again at a later time';
      }

      // @ts-ignore TODO
      const errorMessage = e?.response?.data?.message ?? e?.response?.data?.error;
      if (!errorMessage) {
        throw e;
      }

      // @ts-ignore TODO rewrite this logic
      if ( includes(authErrorStatus, e?.response?.status) &&
           typeof errorMessage === 'string' &&
           includes(authErrors, errorMessage?.toLowerCase() )
      ) {
        await this.clearTokensAndLogout();
      } else {
        throw e;
      }
    }
  }

  // TODO fix types here
  async request(options: IBaseRequestInput) {
    const timeout = {
      timeout: 30000,
    };
    // @ts-ignore TODO
    const response = await this.rawRequest({ ...options, ...timeout });

    return response?.data;
  }

  async signRequest({ headers = {}, ...options }) {
    const h: Headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    };

    return this.request({ headers: h, adapter: 'sigv4', ...options });
  }

  async authRequest({ tokenType = TokenTypes.ID, headers = {}, ...options }: AuthRequestInput) {
    const { tokens } = this.core.api;

    // TODO find out why we are doing this here
    if (headers['public-client-key']) {
      headers['x-api-key'] = headers['public-client-key'];
    }

    const h: Headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    };

    if (tokenType !== 'none') {
      await tokens.freshTokens();
      h.Authorization = `Bearer ${tokens[tokenType]}`;
    }
    return this.request({ headers: h, ...options });
  }

  requestRootResource(options: Record<string, unknown> = {}) {
    return this.requestResource(undefined, options);
  }

  resolveSigningOptions(url: string, config: any): IResolvedSigningOptions {
    const {
      hostname: domain,
      pathname: path,
    } = new URL(url);

    const role: string | undefined = config?.signing?.role ?? config?.role;
    const headers = { ...config?.headers };

    if (get(config, 'signing.hostMapping', {})[domain]) {
      headers[SIGNING_HOST_HEADER] = config.signing.hostMapping[domain];
    }

    // Note: there are some possible overlapping path replacement edge cases
    // * pathMapping has replacements for /service/path1 and /service/path1/route; resources under /service/path1/route
    //   should probably use the longest matching replacement
    // * pathMapping has replacements for /service/path1 -> /service/path2 and /service/path2 -> /service/path3,
    //   and has a resource at /service/path1
    const pathMappings = get(config, 'signing.pathMapping', {});
    for (const [key, value] of Object.entries(pathMappings)) {
      const from = key.replace(new RegExp('(^/|/$)', 'g'), '');
      const to = (value as string).replace(new RegExp('(^/|/$)', 'g'), '');
      const newPath = path.replace(new RegExp(`^/${from}(/.*)?$`), `/${to}$1`);
      if (newPath !== path) {
        headers[SIGNING_PATH_HEADER] = newPath;
      }
    }

    return {
      role,
      headers,
    };
  }

  async resolveUrlParams(methodName?: string, opts: RequestOptions = {}): Promise<IUrlParams> {
    const { params = {}, query, queryOptions } = opts;
    let queryString;
    if (query && queryOptions) {
      queryString = qs.stringify(query, queryOptions);
    } else if (query) {
      queryString = qs.stringify(query);
    } else {
      queryString = '';
    }

    const config = await this.config();

    const urlTemplateString = methodName ? get(config.resources, methodName) : config.resource;

    const template = templ(urlTemplateString, {
      interpolate: URL_INTERPOLATION_RULES,
    });

    const url = `${template(params)}${queryString ? '?' + queryString : ''}`;

    const signingOptions = this.resolveSigningOptions(url, config);
    const role = signingOptions.role;

    const headers = {
      ...(config?.headers),
      ...signingOptions.headers,
    };

    return {
      url,
      headers,
      role,
    };
  }

  async resolveUrl(methodName?: string, opts: RequestOptions = {}): Promise<string> {
    const params = await this.resolveUrlParams(methodName, opts);
    return params.url;
  }

  // TODO define methodNames
  async requestResource(methodName?: string, opts: RequestOptions = {}) {
    const { params = {}, query, queryOptions, headers: h, sign, ...options } = opts;
    const urlParams = await this.resolveUrlParams(methodName, opts);
    /* @ts-ignore */ // TODO fixme
    const headers = h || {};
    merge(headers, urlParams.headers);
    const role = urlParams.role;

    if (sign) {
      return this.signRequest({ url: urlParams.url, role, headers, ...options });
    } else {
      return this.authRequest({ url: urlParams.url, headers, ...options });
    }
  }


}

const URL_INTERPOLATION_RULES = /{([\s\S]+?)}/g;
